import React, { useState } from 'react';
import { Modal } from '../modals';
import { Input, Button } from '../ui';
import { PasswordDisplayModal } from './PasswordDisplayModal';
import { UserCreateResponse } from '../../types';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { username: string; is_superuser: boolean }) => Promise<UserCreateResponse>;
  onUserCreated?: () => void;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onUserCreated,
}) => {
  const [username, setUsername] = useState('');
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Password display modal state
  const [createdUser, setCreatedUser] = useState<{ username: string; password: string } | null>(null);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await onSubmit({ username: username.trim(), is_superuser: isSuperuser });
      // Show password display modal
      setCreatedUser({
        username: result.username,
        password: result.generated_password
      });
      resetForm();
      onClose();
      onUserCreated?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setIsSuperuser(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isValid = username.trim();

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Create New User"
        size="sm"
        footer={
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              onClick={handleSubmit}
              disabled={!isValid || loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
              />
            </div>

            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  A secure password will be automatically generated. You'll be shown the password once after creation.
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_superuser"
                checked={isSuperuser}
                onChange={(e) => setIsSuperuser(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              />
              <label
                htmlFor="is_superuser"
                className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                Grant admin privileges
              </label>
            </div>
          </div>
        </form>
      </Modal>

      {/* Password Display Modal */}
      {createdUser && (
        <PasswordDisplayModal
          isOpen={true}
          onClose={() => setCreatedUser(null)}
          username={createdUser.username}
          password={createdUser.password}
        />
      )}
    </>
  );
};
